import _ from "lodash";
import { usePopupState } from "material-ui-popup-state/hooks";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { ProgressBar } from "src/ui/shared/ProgressBar";

import { fetchFileDownloadLinks } from "@dashboard/products/store";
import { selectProductById } from "@dashboard/products/store/selectors/products";
import { Build } from "@dashboard/products/types";
import { Box, Popover, Typography, useTheme } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { getArtifactIcon } from "../Artifacts";
import { useStyles } from "../Cells.style";

import { useExportFile } from "./DownloadActionsMenu.utils";

export const DownloadActionsMenu = (props: { build: Build }) => {
    const [isLoading, setIsLoading] = React.useState(false);

    const theme = useTheme();
    const dispatch = useDispatch();
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["common", "modals"]);
    const exportFile = useExportFile(props.build);

    const menuAnchorRef = React.useRef<HTMLButtonElement>(null);
    const popupState = usePopupState({ variant: "popover", popupId: "file-download-menu" });

    const product = useSelector((state) => selectProductById(state, props.build.productId));

    const handleFetchLink = async () => {
        try {
            await dispatch(fetchFileDownloadLinks(props.build)).unwrap();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };
    const handlePopoverOpen = async () => {
        setIsLoading(true);
        try {
            await handleFetchLink();
            popupState.open();
        } finally {
            setIsLoading(false);
        }
    };

    const downloadableArtifacts = props.build.artifacts.filter((item) => item.downloadable && item.isUploaded);
    const sortedArtifacts = _.orderBy(downloadableArtifacts, "name");

    return (
        <React.Fragment>
            <Tooltip
                title={
                    props.build.buildStatus !== "succeeded"
                        ? t("common:section.downloadActionsMenu.tooltip.buildDidNotCompleteSuccessfully")
                        : t("common:section.downloadActionsMenu.tooltip.downloadResults")
                }
            >
                <SecondaryButton
                    ref={menuAnchorRef}
                    onClick={handlePopoverOpen}
                    disabled={props.build.buildStatus !== "succeeded" || isLoading}
                    loading={isLoading}
                >
                    <i className="fa-solid fa-download" />
                </SecondaryButton>
            </Tooltip>
            <Popover
                open={popupState.isOpen}
                anchorEl={menuAnchorRef.current}
                onClose={() => popupState.close()}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Box className={classes.popover}>
                    <Box className={classes.popoverHeader}>
                        <Typography variant="h6" style={{ fontWeight: 600, fontSize: "1rem" }}>
                            {t("common:section.downloadActionsMenu.buildResults")}
                        </Typography>
                    </Box>
                    <Box className={classes.popoverContent}>
                        {(() => {
                            if (sortedArtifacts.length === 0) {
                                return (
                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        style={{ padding: theme.spacing(2), textAlign: "center" }}
                                    >
                                        {t("common:section.downloadActionsMenu.nothingToDownload")}
                                    </Typography>
                                );
                            }

                            return sortedArtifacts.map((item) => {
                                const { icon, tooltip } = getArtifactIcon(item);

                                if (!item.link) {
                                    return (
                                        <Tooltip
                                            key={item.id}
                                            title={t("common:section.downloadActionsMenu.tooltip.linkUnavailable")}
                                        >
                                            <Box className={cx(classes.artifactItem, classes.artifactItemDisabled)}>
                                                <i className={`${icon} ${classes.artifactIcon}`} />
                                                <Typography className={classes.artifactName} variant="body1">
                                                    {item.name}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    );
                                }

                                return (
                                    <Box
                                        key={item.id}
                                        component="a"
                                        href={item.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={classes.artifactItem}
                                    >
                                        <Tooltip title={tooltip}>
                                            <i className={`${icon} ${classes.artifactIcon}`} />
                                        </Tooltip>
                                        <Typography className={classes.artifactName} variant="body1">
                                            {item.name}
                                        </Typography>
                                    </Box>
                                );
                            });
                        })()}
                        {product?.visibility?.includes("ExportableBuilds") ? (
                            <Box className={classes.exportSection}>
                                <SecondaryButton
                                    onClick={() => {
                                        popupState.close();
                                        exportFile.onExport();
                                    }}
                                    className={classes.exportButton}
                                >
                                    <i className="fa-solid fa-file-arrow-down" style={{ marginRight: theme.spacing(1) }} />
                                    {t("common:button.export")}
                                </SecondaryButton>
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </Popover>
            <Modal
                open={!!exportFile.name}
                title={t("modals:downloadingFiles.title")}
                endButton={<SecondaryButton onClick={exportFile.onAbort}>{t("common:button.cancel")}</SecondaryButton>}
            >
                <Box style={{ display: "flex", flexDirection: "column", gap: theme.spacing(1) }}>
                    <Typography variant="body1">{exportFile.name}</Typography>
                    <ProgressBar progress={exportFile.progress} color={theme.palette.emteria.primary} />
                    <Typography variant="body1">{exportFile.speed}</Typography>
                </Box>
            </Modal>
        </React.Fragment>
    );
};
